import chain, {
    cloneDeep,
    filter,
    find,
    findIndex,
    first,
    flatMap,
    get,
    isEmpty,
    map,
    omit,
    pick,
    set,
    some,
} from "lodash"; // See: https://github.com/vercel/next.js/issues/52188
import { FilterMatchMode, PrimeIcons } from "primereact/api";
import { Button } from "primereact/button";
import { Chip } from "primereact/chip";
import { Column, ColumnEditorOptions } from "primereact/column";
import {
    DataTableFilterMeta,
    DataTableRowEditCompleteEvent,
    DataTableRowEditEvent,
} from "primereact/datatable";
import { DropdownChangeEvent } from "primereact/dropdown";
import { MultiSelectChangeEvent } from "primereact/multiselect";
import { DomHandler, classNames } from "primereact/utils";
import { ChangeEvent, ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCompanies } from "../../../../common/api/useCompanies";
import { useRoles } from "../../../../common/api/useRoles";
import {
    DataTable,
    Dropdown,
    MultiSelect,
} from "../../../../common/components";
import { SearchInputText } from "../../../../common/components/InputText";
import { SORT } from "../../../../common/constants";
import { Entity } from "../../../../common/models";
import { AdministrationLevelEnum } from "../../constants";
import { User } from "../../models/user.model";
import { UserRolesInfoLink } from "../UserRolesInfoLink";

export interface CompanyRoleMeta extends Partial<User["companies"][number]> {
    role: {
        id: string;
        name: string | null;
        administrationLevel: AdministrationLevelEnum;
    };
    locationsFilterData?: string;
    locations?: Required<Entity>[];
}

interface CompaniesAndRolesProps {
    globalRoles?: User["globalRoles"];
    userCompanies?: User["companies"];
    ActionsBody?: (data: CompanyRoleMeta) => ReactNode;
    isEditable?: boolean;
    companyIdsToRemove?: string[];
    isEditingRow?: boolean;
    onChange?: (data: User["companies"]) => void;
    onAddCompany?: VoidFunction;
    onChangeEditRow?: (isEditing: boolean) => void;
}

const CompaniesAndRoles = (props: CompaniesAndRolesProps) => {
    const {
        globalRoles = [],
        userCompanies = [],
        ActionsBody,
        isEditable = false,
        isEditingRow = false,
        companyIdsToRemove = [],
        onChange = () => {},
        onAddCompany = () => {},
        onChangeEditRow = () => {},
    } = props;

    const { t } = useTranslation(["common", "users", "profile"]);

    const [globalFilterValue, setGlobalFilterValue] = useState("");
    const [filters, setFilters] = useState<DataTableFilterMeta>({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    const [editingRole, setEditingRole] = useState<CompanyRoleMeta["role"]>();
    const [editingLocations, setEditingLocations] = useState<
        Required<Entity>[]
    >([]);
    const [selectedCompany, setSelectedCompany] = useState<Required<Entity>>();

    const { companyLevelRoles } = useRoles();
    const { companies } = useCompanies();

    const isGlobalAdmin = !isEmpty(globalRoles);
    const isEditEnabled = isEditable && !isGlobalAdmin;

    const companiesRolesFilterData: CompanyRoleMeta[] = useMemo(() => {
        if (isGlobalAdmin) {
            return [
                {
                    role: {
                        ...globalRoles[0],
                    },
                },
            ];
        }
        return filter(
            map(userCompanies, (company) => ({
                ...company,
                role: {
                    ...omit(company?.userCompanyRoles[0], "locations"),
                },
                locations: company?.userCompanyRoles[0]?.locations || [],
                locationsFilterData: map(
                    company?.userCompanyRoles[0]?.locations,
                    "name"
                ).join("\n"),
            })),
            (companyRole) => !companyIdsToRemove.includes(companyRole.id!)
        );
    }, [isGlobalAdmin, userCompanies, globalRoles, companyIdsToRemove]);

    const isAddingNewRole = some(companiesRolesFilterData, (companyRole) =>
        isEmpty(`${companyRole.id}`.replaceAll(" ", ""))
    );

    const isDataInvalid = useMemo(() => {
        if (
            isAddingNewRole &&
            (isEmpty(selectedCompany) || isEmpty(editingRole))
        ) {
            return true;
        }
        return (
            editingRole?.administrationLevel ===
                AdministrationLevelEnum.LOCATION && isEmpty(editingLocations)
        );
    }, [editingLocations, editingRole, isAddingNewRole, selectedCompany]);

    useEffect(() => {
        if (isAddingNewRole) {
            // Workaround to click the edit icon for the last row
            first(
                DomHandler.find(
                    document.body,
                    ".companies-and-roles tr:first-of-type button.p-row-editor-init"
                )
            )?.click();
        }
    }, [isAddingNewRole]);

    const onGlobalFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        let newFilters = { ...filters };

        set(newFilters, "global.value", value);
        setFilters(newFilters);
        setGlobalFilterValue(value);
    };

    const onRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
        onChangeEditRow(false);
        if (isDataInvalid || isEmpty(editingRole)) {
            return;
        }
        const { data } = e;
        const newCompanies = cloneDeep(userCompanies);
        const index = findIndex(newCompanies, { id: data.id });

        if (isAddingNewRole && !isEmpty(selectedCompany)) {
            newCompanies[index] = {
                ...selectedCompany,
                userCompanyRoles: [],
            };
        }
        newCompanies[index].userCompanyRoles[0] = {
            ...editingRole,
            locations: editingLocations,
        };
        onChange(newCompanies);
    };

    const onRowEditInit = (e: DataTableRowEditEvent) => {
        const { data } = e;
        onChangeEditRow(true);
        setSelectedCompany(undefined);
        setEditingRole(data.role);
        setEditingLocations(data.locations);
    };

    const onRowEditCancel = () => onChangeEditRow(false);

    const CompanyEditor = () => {
        const companiesOptions = chain(companies)
            .filter(({ id }) => !some(userCompanies, { id }))
            .map((company) => pick(company, ["id", "name"]))
            .value();

        const handleOnChange = (e: DropdownChangeEvent) => {
            setEditingLocations([]);
            setSelectedCompany(e.value);
        };

        return (
            <Dropdown
                value={selectedCompany}
                options={companiesOptions}
                optionLabel="name"
                placeholder={t("common:selectCompany") || ""}
                onChange={handleOnChange}
                className="w-full"
            />
        );
    };

    const RoleEditor = () => {
        const handleOnChange = (e: DropdownChangeEvent) => {
            setEditingRole(e.value);
            if (
                e.value?.administrationLevel === AdministrationLevelEnum.COMPANY
            ) {
                setEditingLocations([]);
            }
        };

        return (
            <Dropdown
                value={editingRole}
                options={companyLevelRoles}
                optionLabel="name"
                placeholder={t("common:selectRole") || ""}
                onChange={handleOnChange}
                className="w-full"
            />
        );
    };

    const LocationsEditor = (options: ColumnEditorOptions) => {
        const { rowData } = options;

        if (
            editingRole?.administrationLevel === AdministrationLevelEnum.COMPANY
        ) {
            return <Chip label={t("profile:allLocations") || ""} />;
        }

        const locations = map(
            flatMap([
                get(
                    find(companies, {
                        id: rowData?.id,
                    }),
                    "locations",
                    []
                ),
                get(
                    find(companies, {
                        id: selectedCompany?.id,
                    }),
                    "locations",
                    []
                ),
            ]),
            ({ name, id }) => ({ name, id })
        );

        const handleOnChange = (e: MultiSelectChangeEvent) =>
            setEditingLocations(e.target.value);

        return (
            <MultiSelect
                value={editingLocations}
                options={locations}
                optionLabel="name"
                onChange={handleOnChange}
                display="chip"
                placeholder={t("common:selectLocation") || ""}
                className={classNames("p-0 w-full", {
                    "p-invalid": rowData.id && isEmpty(editingLocations),
                })}
            />
        );
    };

    const Header = () =>
        companiesRolesFilterData.length > 1 && (
            <div className="flex justify-end gap-5">
                <SearchInputText
                    onChange={onGlobalFilterChange}
                    placeholder={t("common:search") || ""}
                    value={globalFilterValue}
                />
            </div>
        );

    const CompanyBody = (data: CompanyRoleMeta) => {
        if (data.role.administrationLevel === AdministrationLevelEnum.GLOBAL) {
            return t("profile:allCompanies");
        }
        return data.name;
    };

    const LocationsBody = (data: CompanyRoleMeta) => {
        switch (data.role.administrationLevel) {
            case AdministrationLevelEnum.GLOBAL:
                return "-";
            case AdministrationLevelEnum.COMPANY:
                return <Chip label={t("profile:allLocations") || ""} />;
            case AdministrationLevelEnum.LOCATION:
                return (
                    <div className="flex flex-row gap-1 flex-wrap">
                        {data.userCompanyRoles?.[0].locations?.map(
                            ({ name, id }) => (
                                <Chip key={id} label={name} />
                            )
                        )}
                    </div>
                );
        }
    };

    return (
        <>
            <div className="flex items-end justify-between gap-6">
                <div>
                    <h3 className="text-lg pt-5 pb-2.5">
                        {t("profile:companiesAndRoles")}
                    </h3>
                    <UserRolesInfoLink />
                </div>
                {isEditEnabled && (
                    <Button
                        icon={PrimeIcons.PLUS}
                        severity="secondary"
                        label={t("common:add") || ""}
                        iconPos="right"
                        disabled={isAddingNewRole || isEditingRow}
                        onClick={onAddCompany}
                    />
                )}
            </div>
            {!isEmpty(companiesRolesFilterData) && (
                <DataTable
                    className="companies-and-roles"
                    pt={{
                        header: {
                            className: "!bg-transparent !border-0 !px-0",
                        },
                    }}
                    emptyMessage={t("common:noResultsFound")}
                    value={companiesRolesFilterData}
                    sortField="name"
                    sortOrder={SORT.ASC}
                    filters={filters}
                    header={Header}
                    scrollable
                    scrollHeight="15rem"
                    editMode={isEditEnabled ? "row" : undefined}
                    onRowEditComplete={onRowEditComplete}
                    onRowEditInit={onRowEditInit}
                    onRowEditCancel={onRowEditCancel}
                >
                    <Column
                        field="name"
                        header={t("common:company")}
                        body={CompanyBody}
                        editor={isAddingNewRole ? CompanyEditor : null}
                        sortable
                        className="font-semibold"
                    />
                    <Column
                        field="role"
                        filterField="role.name"
                        header={t("profile:role")}
                        body={(data: CompanyRoleMeta) => data.role.name}
                        editor={isEditEnabled ? RoleEditor : null}
                    />
                    <Column
                        field="locations"
                        filterField="locationsFilterData"
                        header={t("common:location")}
                        body={LocationsBody}
                        editor={isEditEnabled ? LocationsEditor : null}
                    />
                    <Column
                        rowEditor
                        pt={{
                            rowEditorInitButton: {
                                className:
                                    isAddingNewRole || isEditingRow
                                        ? "hidden"
                                        : "",
                            },
                            rowEditorSaveButton: {
                                className: isDataInvalid ? "hidden" : "",
                            },
                            rowEditorCancelButton: {
                                className: isAddingNewRole ? "hidden" : "",
                            },
                        }}
                    />
                    <Column body={ActionsBody} />
                </DataTable>
            )}
        </>
    );
};

export default CompaniesAndRoles;
